import { useRouter } from "next/router";
import { useEffect } from "react";
import use from "react-promise-suspense";
import { graphql, useFragment } from "react-relay";

import { checkIsContainsPlusQueryString } from "scmp-app/components/article/article-render/plus/helpers";
import { checkIsPostiesArticle } from "scmp-app/components/article/article-render/posties/helpers";
import { eventEmitter } from "scmp-app/lib/rosetta/event-emitter";
import { isSsr } from "scmp-app/lib/utils";
import type { hooksPaywallPromiseContent$key } from "scmp-app/queries/__generated__/hooksPaywallPromiseContent.graphql";

import { usePaywallCheckerContext } from "./contexts";

export const usePaywallPromise = (content_: hooksPaywallPromiseContent$key) => {
  const { setPaywallMap } = usePaywallCheckerContext();
  const router = useRouter();

  const content = useFragment(
    graphql`
      fragment hooksPaywallPromiseContent on Content {
        entityId
        ...helpersCheckIsPostiesArticle
      }
    `,
    content_,
  );

  const isPosties = checkIsPostiesArticle(content);
  const isPlus = checkIsContainsPlusQueryString(router.query);

  const isShowInlinePaywall = use(
    (_entityId: string, _: string) => {
      if (isSsr() || isPosties || isPlus) return Promise.resolve(undefined);
      return new Promise<boolean>(resolve => {
        const handler = (value: boolean) => {
          resolve(value);
          eventEmitter.removeListener("showInlineWall", handler);
        };
        eventEmitter.on("showInlineWall", handler);
      });
    },
    [content.entityId, "usePaywallPromise"],
  );

  useEffect(() => {
    if (isShowInlinePaywall === undefined) return;
    setPaywallMap?.(paywallMap => ({
      ...paywallMap,
      [content.entityId]: isShowInlinePaywall,
    }));
  }, [content.entityId, isShowInlinePaywall, setPaywallMap]);

  return isShowInlinePaywall;
};
